import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'casarenovaplus_welcome_popup_seen';
const DELAY_MS = 3500;
const OFFER_DURATION_MINUTES = 15;

function getTimeLeft(expiresAt: number) {
  const diff = Math.max(0, expiresAt - Date.now());
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { minutes, seconds, expired: diff === 0 };
}

export default function WelcomePopup() {
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState({ minutes: OFFER_DURATION_MINUTES, seconds: 0, expired: false });

  useEffect(() => {
    const alreadySeen = sessionStorage.getItem(STORAGE_KEY);
    if (alreadySeen) return;

    const timer = setTimeout(() => {
      const expiry = Date.now() + OFFER_DURATION_MINUTES * 60 * 1000;
      setExpiresAt(expiry);
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });
    }, DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible || expiresAt === 0) return;
    const interval = setInterval(() => {
      const tl = getTimeLeft(expiresAt);
      setTimeLeft(tl);
      if (tl.expired) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [visible, expiresAt]);

  const handleClose = useCallback(() => {
    setAnimateIn(false);
    setTimeout(() => {
      setVisible(false);
      sessionStorage.setItem(STORAGE_KEY, '1');
    }, 350);
  }, []);

  const handleCTA = useCallback(() => {
    handleClose();
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      else window.location.href = '/#contact';
    }, 400);
  }, [handleClose]);

  if (!visible) return null;

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-350 ${
        animateIn ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className={`relative w-full max-w-lg bg-white rounded-2xl overflow-hidden transition-all duration-350 ${
          animateIn ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-6'
        }`}
      >
        {/* Header image band */}
        <div className="relative h-36 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20luxury%20home%20renovation%20interior%20design%20Geneva%20Switzerland%20elegant%20living%20room%20bright%20natural%20light%20warm%20tones%20professional%20photography%20high%20quality%20architectural%20detail&width=600&height=200&seq=popup-welcome-banner-hb&orientation=landscape"
            alt="HB Services offre spéciale"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span className="inline-flex items-center gap-1.5 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full mb-2 uppercase tracking-wider">
              <i className="ri-gift-line"></i>
              Offre exclusive
            </span>
            <h2 className="text-2xl font-black text-white leading-tight">
              Bienvenue chez CasaReNovaPlus !
            </h2>
          </div>
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/30 hover:bg-black/50 text-white rounded-full transition-colors cursor-pointer"
          aria-label="Fermer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Body */}
        <div className="px-7 py-6">
          <p className="text-gray-600 text-sm leading-relaxed text-center mb-5">
            Pour vous remercier de votre visite, bénéficiez d'une remise exceptionnelle sur votre première intervention à Neuenegg et en Suisse romande.
          </p>

          {/* Offer highlight */}
          <div className="bg-gradient-to-r from-teal-50 to-brand-50 border border-teal-200 rounded-xl p-4 mb-5 text-center">
            <p className="text-xs text-teal-600 font-semibold uppercase tracking-wider mb-1">Votre avantage</p>
            <p className="text-4xl font-black text-teal-700 leading-none">-20%</p>
            <p className="text-sm text-gray-600 mt-1">sur votre première intervention</p>
          </div>

          {/* Countdown */}
          {!timeLeft.expired ? (
            <div className="mb-5">
              <p className="text-xs text-gray-500 text-center mb-2 font-medium">
                <i className="ri-time-line mr-1 text-red-500"></i>
                Offre valable encore :
              </p>
              <div className="flex items-center justify-center gap-3">
                <div className="flex flex-col items-center bg-gray-900 text-white rounded-xl px-4 py-2.5 min-w-[60px]">
                  <span className="text-2xl font-black tabular-nums leading-none">{pad(timeLeft.minutes)}</span>
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">min</span>
                </div>
                <span className="text-2xl font-black text-gray-400 mb-1">:</span>
                <div className="flex flex-col items-center bg-gray-900 text-white rounded-xl px-4 py-2.5 min-w-[60px]">
                  <span className="text-2xl font-black tabular-nums leading-none">{pad(timeLeft.seconds)}</span>
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">sec</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-5 text-center">
              <p className="text-sm text-red-500 font-semibold">
                <i className="ri-error-warning-line mr-1"></i>
                L'offre a expiré — contactez-nous quand même !
              </p>
            </div>
          )}

          {/* CTA */}
          <button
            type="button"
            onClick={handleCTA}
            className="w-full rounded-xl bg-gradient-to-r from-brand-500 to-teal-600 text-white font-bold py-3.5 text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-arrow-right-circle-line text-lg"></i>
            Profiter de l'offre maintenant
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="w-full mt-2.5 text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer py-1"
          >
            Non merci, je refuse cette offre
          </button>
        </div>
      </div>
    </div>
  );
}
